import { useState } from 'react'
import { Button } from '#app/components/ui/button'
import { Icon } from '#app/components/ui/icon'
import { InputText } from '#app/components/ui/input-text'

interface DetallesData {
	pasosElaboracion: string
	controlCalidad: string
	fechaVencimiento: string
	cantidadAnual: string
	aniosMercado: string
	productosDiscontinuados: string
	areaDistribucion: string
	exportacion: string
	certificaciones: string
	planRetiro: string
}

interface RCProductosDetallesScreenProps {
	onNext: (data: DetallesData) => void
	onBack: () => void
	data?: Partial<DetallesData>
}

export function RCProductosDetallesScreen({
	onNext,
	onBack,
	data,
}: RCProductosDetallesScreenProps) {
	const [formData, setFormData] = useState<DetallesData>({
		pasosElaboracion: data?.pasosElaboracion || '',
		controlCalidad: data?.controlCalidad || '',
		fechaVencimiento: data?.fechaVencimiento || '',
		cantidadAnual: data?.cantidadAnual || '',
		aniosMercado: data?.aniosMercado || '',
		productosDiscontinuados: data?.productosDiscontinuados || '',
		areaDistribucion: data?.areaDistribucion || '',
		exportacion: data?.exportacion || '',
		certificaciones: data?.certificaciones || '',
		planRetiro: data?.planRetiro || '',
	})

	const handleSubmit = () => {
		onNext(formData)
	}

	return (
		<div className="flex h-full w-full flex-col">
			<div className="flex-1 overflow-y-auto">
				<div className="flex w-full flex-col items-center gap-6">
					<h2 className="font-parkinsans text-center text-2xl leading-8 font-medium text-blue-800">
						Detalles de Producción
					</h2>
					<p className="text-center text-sm text-neutral-600">
						Información sobre elaboración, control de calidad y distribución.
						Todos los campos son opcionales.
					</p>

					<div className="flex w-full flex-col gap-4">
						<div className="flex flex-col gap-2">
							<label className="text-sm font-medium text-neutral-700">
								Proceso de Elaboración
							</label>
							<p className="text-xs text-neutral-500">
								Describí los pasos del proceso y personas involucradas
							</p>
							<textarea
								className="w-full rounded-xl border border-neutral-300 px-4 py-3 text-base"
								rows={3}
								placeholder="Ej: Recepción de materia prima → Control de calidad → Procesamiento → Envasado → Almacenamiento"
								value={formData.pasosElaboracion}
								onChange={(e) =>
									setFormData({ ...formData, pasosElaboracion: e.target.value })
								}
							/>
						</div>

						<div className="flex flex-col gap-2">
							<label className="text-sm font-medium text-neutral-700">
								Control de Calidad
							</label>
							<p className="text-xs text-neutral-500">
								Responsable, tests realizados, conservación (si aplica)
							</p>
							<textarea
								className="w-full rounded-xl border border-neutral-300 px-4 py-3 text-base"
								rows={3}
								placeholder="Ej: Responsable: Ing. Juan Pérez. Tests: análisis microbiológico, control de temperatura, inspección visual."
								value={formData.controlCalidad}
								onChange={(e) =>
									setFormData({ ...formData, controlCalidad: e.target.value })
								}
							/>
						</div>

						<div className="grid grid-cols-2 gap-4">
							<InputText
								label="Fecha de Vencimiento"
								labelPosition="internal"
								size="l"
								placeholder="Ej: 12 meses"
								value={formData.fechaVencimiento}
								onChange={(e) =>
									setFormData({ ...formData, fechaVencimiento: e.target.value })
								}
							/>

							<InputText
								label="Cantidad Anual Producida"
								labelPosition="internal"
								size="l"
								placeholder="Ej: 50.000 unidades"
								value={formData.cantidadAnual}
								onChange={(e) =>
									setFormData({ ...formData, cantidadAnual: e.target.value })
								}
							/>
						</div>

						<div className="grid grid-cols-2 gap-4">
							<InputText
								label="Años en el Mercado"
								labelPosition="internal"
								size="l"
								placeholder="Ej: 15 años"
								value={formData.aniosMercado}
								onChange={(e) =>
									setFormData({ ...formData, aniosMercado: e.target.value })
								}
							/>

							<InputText
								label="Productos Discontinuados"
								labelPosition="internal"
								size="l"
								placeholder="Ej: Ninguno / Producto X"
								value={formData.productosDiscontinuados}
								onChange={(e) =>
									setFormData({
										...formData,
										productosDiscontinuados: e.target.value,
									})
								}
							/>
						</div>

						<InputText
							label="Área de Distribución"
							labelPosition="internal"
							size="l"
							placeholder="Ej: Nacional, AMBA, Provincia de Buenos Aires"
							value={formData.areaDistribucion}
							onChange={(e) =>
								setFormData({ ...formData, areaDistribucion: e.target.value })
							}
						/>

						<InputText
							label="Exportación (países)"
							labelPosition="internal"
							size="l"
							placeholder="Ej: Uruguay, Chile, Paraguay / No exporta"
							value={formData.exportacion}
							onChange={(e) =>
								setFormData({ ...formData, exportacion: e.target.value })
							}
						/>

						<InputText
							label="Certificaciones / Sellos Oficiales"
							labelPosition="internal"
							size="l"
							placeholder="Ej: ISO 9001, HACCP, Sello Alimentos Argentinos"
							value={formData.certificaciones}
							onChange={(e) =>
								setFormData({ ...formData, certificaciones: e.target.value })
							}
						/>

						<div className="flex flex-col gap-2">
							<label className="text-sm font-medium text-neutral-700">
								Plan de Retiro de Productos
							</label>
							<p className="text-xs text-neutral-500">
								Procedimiento ante problemas con productos
							</p>
							<textarea
								className="w-full rounded-xl border border-neutral-300 px-4 py-3 text-base"
								rows={3}
								placeholder="Ej: Protocolo de recall establecido, comunicación a clientes vía email/teléfono, retiro en 48hs"
								value={formData.planRetiro}
								onChange={(e) =>
									setFormData({ ...formData, planRetiro: e.target.value })
								}
							/>
						</div>
					</div>
				</div>
			</div>

			<div className="flex w-full shrink-0 flex-col items-center justify-start gap-2 bg-white pt-4 md:flex-row md:justify-between md:bg-transparent md:pt-8">
				<Button
					variant="primary"
					appearance="ghost"
					size="l"
					onClick={onBack}
					className="order-2 w-full gap-2 rounded-xl px-4 py-3 text-sm leading-4 md:order-1 md:w-auto md:px-6 md:py-4 md:text-base md:leading-5"
				>
					<Icon name="arrow-left" className="size-4" />
					Atrás
				</Button>
				<Button
					variant="secondary"
					appearance="solid"
					size="l"
					onClick={handleSubmit}
					className="order-1 w-full md:order-2 md:w-auto"
				>
					Enviar Cotización
					<Icon name="arrow-right" className="size-3.5" />
				</Button>
			</div>
		</div>
	)
}
